import { z } from "zod";
import { ulidParam } from "./ids";

// ===== Marketplaces: Mercado Livre (T1.16), Shopee (T1.17) e canais (T3.3), RF-VEN-09 =====

/** Canais da T3.3 (fluxo de autorização genérico: `/marketplaces/channels/{provider}/…`). */
export const CHANNEL_PROVIDERS = ["magalu", "amazon", "nuvemshop", "shopify"] as const;
export type ChannelProviderId = (typeof CHANNEL_PROVIDERS)[number];
export const MARKETPLACE_PROVIDERS = ["mercadolivre", "shopee", ...CHANNEL_PROVIDERS] as const;
export type MarketplaceProviderId = (typeof MARKETPLACE_PROVIDERS)[number];
export type MarketplaceAccountStatus = "active" | "reauth_required" | "disconnected";
/** Modo do provedor no ambiente: marketplace falso (testes), real ou desligado. */
export type MarketplaceMode = "sandbox" | "live" | "disabled";

/** Configuração da conta conectada (a loja leva o nome). Depósitos repetidos são unificados. */
export const marketplaceAccountSettingsInput = z.object({
  storeName: z
    .string("Informe o nome da loja")
    .trim()
    .min(1, "Informe o nome da loja")
    .max(80, "Até 80 caracteres"),
  importOrders: z.boolean("Use sim ou não"),
  reserveOnImport: z.boolean("Use sim ou não"),
  syncStock: z.boolean("Use sim ou não"),
  stockWarehouseIds: z
    .array(ulidParam)
    .max(20, "No máximo 20 depósitos")
    .transform((ids) => [...new Set(ids)]),
  orderWarehouseId: ulidParam.nullable(),
  syncPrice: z.boolean("Use sim ou não"),
  priceListId: ulidParam.nullable(),
});
export type MarketplaceAccountSettingsInput = z.infer<typeof marketplaceAccountSettingsInput>;

/** Volta do OAuth: `code` do Mercado Livre e o `state` gerado na autorização (32 bytes em base64url). */
export const mlCallbackInput = z.object({
  code: z
    .string("Código ausente")
    .min(1, "Código ausente")
    .max(256, "Código inválido")
    .regex(/^[A-Za-z0-9_.-]+$/, "Código inválido"),
  state: z.string("Retorno inválido").regex(/^[A-Za-z0-9_-]{43}$/, "Retorno inválido"),
});
export type MlCallbackInput = z.infer<typeof mlCallbackInput>;

/** Autorização simulada (sandbox): só o state da autorização em andamento. */
export const mlSandboxAuthorizeInput = mlCallbackInput.pick({ state: true });

/**
 * Volta da autorização da loja na Shopee: `code` e `shop_id` que a Shopee acrescenta ao redirect e o `state` que o
 * ERP pôs na própria URL de retorno (a Shopee não tem `state` nem PKCE).
 */
export const shopeeCallbackInput = z.object({
  code: z
    .string("Código ausente")
    .min(1, "Código ausente")
    .max(128, "Código inválido")
    .regex(/^[A-Za-z0-9]+$/, "Código inválido"),
  shopId: z.string("Loja ausente").regex(/^\d{1,20}$/, "Loja inválida"),
  state: mlCallbackInput.shape.state,
});
export type ShopeeCallbackInput = z.infer<typeof shopeeCallbackInput>;
export const shopeeSandboxAuthorizeInput = mlSandboxAuthorizeInput;

export const channelProviderParam = z.object({ provider: z.enum(CHANNEL_PROVIDERS, "Canal inválido") });

/** Início da autorização de um canal: Shopify pede o endereço da loja (`minhaloja` ou `minhaloja.myshopify.com`). */
export const channelAuthorizeInput = z.object({
  shop: z.string("Informe o endereço da loja").trim().max(120, "Até 120 caracteres").optional(),
});
export type ChannelAuthorizeInput = z.infer<typeof channelAuthorizeInput>;

/**
 * Volta da autorização de um canal: a query inteira do redirect (o servidor lê `code`, `state` e o que o canal manda
 * — `shop`/`hmac` na Shopify, `spapi_oauth_code`/`selling_partner_id` na Amazon — e confere a assinatura).
 */
export const channelCallbackInput = z.object({
  query: z.string("Retorno inválido").min(1, "Retorno inválido").max(2000, "Retorno inválido"),
});
export type ChannelCallbackInput = z.infer<typeof channelCallbackInput>;
export const channelSandboxAuthorizeInput = mlSandboxAuthorizeInput;

export const marketplaceLinkInput = z.object({ productId: ulidParam.nullable() });
export type MarketplaceLinkInput = z.infer<typeof marketplaceLinkInput>;

export const marketplaceListingListQuery = z.object({
  q: z.string().trim().max(100).optional(),
  linked: z.enum(["yes", "no"], "Use yes ou no").optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type MarketplaceListingListQuery = z.infer<typeof marketplaceListingListQuery>;

export const marketplaceOrderListQuery = z.object({
  accountId: ulidParam.optional(),
  review: z.stringbool({ error: "Use true ou false" }).optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type MarketplaceOrderListQuery = z.infer<typeof marketplaceOrderListQuery>;

/**
 * Pedido simulado no marketplace falso (Mercado Livre ou Shopee): novo (anúncio + quantidade) ou mudança de situação de
 * um existente. Situações neutras: na Shopee, pago = READY_TO_SHIP, aguardando = UNPAID, cancelado = CANCELLED.
 */
export const marketplaceSandboxOrderInput = z.object({
  listingId: ulidParam.optional(),
  qty: z.number().int().min(1, "Mínimo 1").max(10, "No máximo 10").default(1),
  status: z.enum(["paid", "payment_required", "cancelled"], "Situação inválida").default("paid"),
  orderId: z
    .string()
    .regex(/^[0-9A-Za-z-]{1,64}$/, "Pedido inválido")
    .optional(),
});
export type MarketplaceSandboxOrderInput = z.infer<typeof marketplaceSandboxOrderInput>;

export type MarketplaceAccountView = {
  id: string;
  provider: MarketplaceProviderId;
  storeId: string;
  storeName: string;
  externalUserId: string;
  nickname: string;
  sandbox: boolean;
  status: MarketplaceAccountStatus;
  importOrders: boolean;
  reserveOnImport: boolean;
  syncStock: boolean;
  stockWarehouseIds: string[];
  orderWarehouseId: string | null;
  syncPrice: boolean;
  priceListId: string | null;
  /** Nome da lista de preço (a lista de custo aparece pelo nome mesmo para quem não pode escolhê-la). */
  priceListName: string | null;
  lastPollAt: string | null;
  lastError: string | null;
  /** Vencimento da autorização da loja (Shopee: até 1 ano); null = sem prazo conhecido. */
  authExpiresAt: string | null;
  listings: { total: number; linked: number; pending: number; errors: number };
  ordersInReview: number;
  createdAt: string;
  disconnectedAt: string | null;
};

export type MarketplaceListingView = {
  id: string;
  accountId: string;
  externalItemId: string;
  externalVariationId: string | null;
  title: string;
  variationLabel: string | null;
  sku: string | null;
  priceCents: number | null;
  availableQty: number | null;
  status: string | null;
  listingType: string | null;
  fulfillment: boolean;
  permalink: string | null;
  product: { id: string; name: string; sku: string | null } | null;
  syncPending: boolean;
  syncError: string | null;
  lastStockSent: number | null;
  lastPriceSentCents: number | null;
  syncedAt: string | null;
};

export type MarketplaceOrderItemSummary = {
  itemId: string;
  variationId: string | null;
  title: string;
  qty: number;
  unitPriceCents: number;
  productId: string | null;
};

export type MarketplaceOrderView = {
  id: string;
  accountId: string;
  storeName: string;
  externalOrderId: string;
  packId: string | null;
  status: string;
  dateCreated: string;
  lastUpdated: string;
  totalCents: number;
  items: MarketplaceOrderItemSummary[];
  salesOrder: { id: string; number: number } | null;
  reviewReason: string | null;
  importedAt: string;
};

export type MarketplaceSyncResult = { scheduled: boolean; pending: number };

/** `GET /marketplaces/accounts`: `mode` é o do Mercado Livre (compatibilidade da T1.16); `modes`, de cada provedor. */
export type MarketplaceAccountsResponse = {
  accounts: MarketplaceAccountView[];
  mode: MarketplaceMode;
  modes: Record<MarketplaceProviderId, MarketplaceMode>;
};
